import { useId, useState } from 'react';
import type { BuildingData, Floor } from '@/data/floorPlans';

interface Props {
  data: BuildingData;
  floor: Floor;
  selectedRoomId: string | null;
  highlightedRoomIds?: Set<string>;
  onRoomClick: (roomId: string) => void;
}

/** Room geometry is supplied by the existing, unverified schematic dataset. */
export default function FloorPlanSVG({
  data, floor, selectedRoomId, highlightedRoomIds, onRoomClick,
}: Props) {
  const instanceId = useId().replace(/:/g, '');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const { width, height } = data.building.viewBox;
  const gridId = `campus-grid-${instanceId}`;
  const titleId = `campus-title-${instanceId}`;
  const descriptionId = `campus-description-${instanceId}`;

  return (
    <svg
      viewBox={`-20 -20 ${width + 40} ${height + 40}`}
      role="group"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      style={{ width: '100%', height: '100%', display: 'block', background: '#171e21' }}
      preserveAspectRatio="xMidYMid meet"
    >
      <title id={titleId}>{`${data.building.name} — ${floor.label} room schematic`}</title>
      <desc id={descriptionId}>
        Approximate room layout, not a verified floor plan or navigation guide.
        Tab to a room and press Enter or Space to inspect its details.
      </desc>
      <defs>
        <pattern id={gridId} width="24" height="24" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.8" fill="#d9e2d9" opacity="0.09" />
        </pattern>
      </defs>

      <rect x="-20" y="-20" width={width + 40} height={height + 40} fill={`url(#${gridId})`} aria-hidden="true" />
      <rect
        x={floor.outline.x} y={floor.outline.y}
        width={floor.outline.w} height={floor.outline.h}
        fill="#1b2326"
        stroke="#67716d" strokeWidth="1"
        vectorEffect="non-scaling-stroke"
        aria-hidden="true"
      />

      {floor.corridor && (
        <rect
          x={floor.corridor.from}
          y={floor.corridor.y - floor.corridor.width / 2}
          width={floor.corridor.to - floor.corridor.from}
          height={floor.corridor.width}
          fill="#242c2e"
          stroke="#404b4a" strokeWidth="0.5"
          vectorEffect="non-scaling-stroke"
          aria-hidden="true"
        />
      )}

      {floor.rooms.map(room => {
        const isSelected = room.roomId === selectedRoomId;
        const isHighlighted = highlightedRoomIds?.has(room.roomId) ?? false;
        const isHovered = room.roomId === hoveredId;
        const isFocused = room.roomId === focusedId;
        const isActive = isSelected || isHighlighted;
        const fill = isSelected ? '#28463f' : isHighlighted ? '#243933' : isHovered || isFocused ? '#303a3b' : '#232c2e';
        const stroke = isFocused ? '#e4fff0' : isSelected ? '#9ddac5' : isHighlighted ? '#73a892' : isHovered ? '#bbc5bd' : '#7b8780';
        const shapeStyle = {
          fill,
          stroke,
          strokeWidth: isFocused ? 2 : isSelected ? 1.6 : 0.85,
          strokeDasharray: isFocused ? '5 3' : undefined,
        };

        return (
          <g
            key={room.roomId}
            role="button"
            tabIndex={0}
            aria-label={`${room.roomId}, ${room.type}`}
            aria-pressed={isSelected}
            onMouseEnter={() => setHoveredId(room.roomId)}
            onMouseLeave={() => setHoveredId(null)}
            onFocus={() => setFocusedId(room.roomId)}
            onBlur={() => setFocusedId(null)}
            onClick={() => onRoomClick(room.roomId)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onRoomClick(room.roomId);
              }
            }}
            style={{ cursor: 'pointer', outline: 'none' }}
          >
            <title>{`${room.roomId} · ${room.type}`}</title>
            {room.shape.type === 'rect' ? (
              <rect
                x={room.shape.x} y={room.shape.y}
                width={room.shape.w} height={room.shape.h}
                {...shapeStyle}
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <polygon points={room.shape.points} {...shapeStyle} vectorEffect="non-scaling-stroke" />
            )}
            <text
              x={room.labelAnchor.x} y={room.labelAnchor.y - 3}
              textAnchor="middle"
              fontFamily="'Space Grotesk', 'Inter', sans-serif"
              fontSize="20" fontWeight="500" letterSpacing="-0.4"
              fill={isActive ? '#d8f6e8' : '#e6e8e1'}
              pointerEvents="none"
            >{room.roomId}</text>
            <text
              x={room.labelAnchor.x} y={room.labelAnchor.y + 18}
              textAnchor="middle"
              fontFamily="'Inter', sans-serif"
              fontSize="10" letterSpacing="0.4"
              fill={isActive ? '#a6cfbd' : '#a2afa7'}
              pointerEvents="none"
            >{room.type}</text>
            {isSelected && (
              <line
                x1={room.labelAnchor.x - 12} x2={room.labelAnchor.x + 12}
                y1={room.labelAnchor.y + 32} y2={room.labelAnchor.y + 32}
                stroke="#b5e2cf" strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}
